/*
 * bc-price-total
 *
 * Rewrites the main <product-price> with either the unit price or the
 * "qty × unit = total" math line, depending on the current quantity. The
 * sticky add-to-cart bar's price clone always shows just the unit price
 * (no math line — the bar is height-constrained). Runs on every product
 * page via snippets/bc-price-total.liquid.
 *
 * Pricing model:
 *   - Variant prices come from the local `script[data-bc-prices]` map
 *     emitted alongside this element so the helper is self-contained
 *     (no dependency on bc-variant-optimistic, which doesn't render
 *     for single-variant products).
 *   - Standard mode: qty is read from the standard quantity input and
 *     "unit" is variant.price.
 *   - bc-custom-length custom mode: qty is the local pieces input on
 *     <bc-custom-length> (the standard quantity input in that mode
 *     holds length × pieces — the cart quantity, not the customer-
 *     facing piece count), and "unit" is the per-piece price
 *     (variant.price × length per piece). Length-input changes are
 *     surfaced via the `bc:custom-length:change` event.
 *
 * Write timing: the price slots are also written by bc-variant-optimistic
 * (synchronously on variant change). We schedule via requestAnimationFrame
 * so our writes land after it, regardless of listener registration order.
 *
 * To avoid clobbering Horizon's own server-rendered prices in cases this
 * helper can't compute (no variant id, no matching price), `_sync` bails
 * silently — leaving whatever text is already in the .price slot.
 */
(() => {
  if (customElements.get('bc-price-total')) return;

  class BcPriceTotal extends HTMLElement {
    connectedCallback() {
      // Match the same scope ladder bc-custom-length / bc-variant-optimistic
      // use so the quick-add modal copy operates on its own variant payload
      // and own product-price elements (not the page's).
      this.scope =
        this.closest('quick-add-dialog') ||
        this.closest('.shopify-section') ||
        this.closest('product-information') ||
        document;

      this.locale = this.dataset.locale || 'en';
      this.currency = this.dataset.currency || 'USD';
      this.formatter = window.bcMoney.formatter(this.locale, this.currency);

      this.variantPrices = {};
      try {
        const script = this.querySelector('script[data-bc-prices]');
        if (script) {
          this.variantPrices = JSON.parse(script.textContent || '{}');
        }
      } catch (_e) {
        // Empty map → _sync bails silently.
      }

      this._onVariantUpdate = () => this._scheduleSync();
      this._onQuantityUpdate = () => this._scheduleSync();
      this._onCustomLengthChange = () => this._scheduleSync();

      document.addEventListener('variant:update', this._onVariantUpdate);
      this.scope.addEventListener('quantity-selector:update', this._onQuantityUpdate);
      this.scope.addEventListener('bc:custom-length:change', this._onCustomLengthChange);

      // Initial sync. Server-rendered prices are already correct for the
      // qty=1 / standard case, but a returning visitor whose browser
      // restored qty=3 (or a custom-length default with length>1) needs
      // the math drawn on first paint.
      this._scheduleSync();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:update', this._onVariantUpdate);
      this.scope?.removeEventListener('quantity-selector:update', this._onQuantityUpdate);
      this.scope?.removeEventListener('bc:custom-length:change', this._onCustomLengthChange);
      if (this._scheduled != null) {
        cancelAnimationFrame(this._scheduled);
        this._scheduled = null;
      }
    }

    _scheduleSync() {
      if (this._scheduled != null) return;
      this._scheduled = requestAnimationFrame(() => {
        this._scheduled = null;
        this._sync();
      });
    }

    _isCustomMode() {
      return this.scope?.hasAttribute?.('data-bc-custom-length-active') === true;
    }

    _currentVariantPriceCents() {
      const idInput = this.scope.querySelector('input[name="id"]');
      const id = idInput?.value;
      if (!id) return null;
      const cents = this.variantPrices[id];
      return Number.isFinite(cents) ? cents : null;
    }

    _readState() {
      if (this._isCustomMode()) {
        const lengthInput = this.scope.querySelector(
          'bc-custom-length [data-key="custom-length-input"]'
        );
        const piecesInput = this.scope.querySelector(
          'bc-custom-length [data-key="custom-pieces-input"]'
        );
        const length = parseFloat(lengthInput?.value);
        const pieces = parseInt(piecesInput?.value, 10);
        return {
          mode: 'custom',
          qty: Number.isFinite(pieces) && pieces > 0 ? pieces : 1,
          length: Number.isFinite(length) && length > 0 ? length : 0,
        };
      }
      const qtyInput = this.scope.querySelector('input[name="quantity"]');
      const qty = parseInt(qtyInput?.value, 10);
      return {
        mode: 'standard',
        qty: Number.isFinite(qty) && qty > 0 ? qty : 1,
        length: 0,
      };
    }

    _sync() {
      const cents = this._currentVariantPriceCents();
      if (cents == null) return;

      const { mode, qty, length } = this._readState();

      // Custom mode without a length entered yet has no meaningful unit
      // price to display — leave the server-rendered text alone rather
      // than printing $0.00.
      if (mode === 'custom' && !(length > 0)) return;

      // Anchor the displayed total to what the cart will actually charge.
      // bc-custom-length._customAddToCart submits `Math.ceil(length × pieces)`
      // as the cart-line quantity (Shopify quantities are integers; ceil
      // rounds fractional totals UP so the customer is never undercharged).
      // Mirror the same formula here so PDP and checkout never disagree.
      // Per-piece (unit) is total ÷ pieces, rounded for display only — the
      // total is the source of truth. For integer length × pieces (the
      // common case, since the schema defaults to step:1) the unit equals
      // `cents × length` exactly. For fractional totals, the unit absorbs
      // the ceil slack averaged across pieces (e.g. length=24.1, pieces=3
      // → cart qty 73, unit shows cents×24.333…). That keeps the
      // multiplication `pieces × unit = total` consistent at the cost of
      // a unit that doesn't equal `cents × length`; the alternative (show
      // cents×length as the unit) would break the multiplication, which
      // is worse UX.
      let unitCents, totalCents;
      if (mode === 'custom') {
        const cartQuantity = Math.max(1, Math.ceil(length * qty));
        totalCents = cents * cartQuantity;
        unitCents = qty > 0 ? Math.round(totalCents / qty) : totalCents;
      } else {
        unitCents = cents;
        totalCents = unitCents * qty;
      }

      const unit = this._formatMoney(unitCents);
      const math = qty > 1 ? ` × ${qty} = ${this._formatMoney(totalCents)}` : null;

      this._writePriceSlots(unit, math);
    }

    _formatMoney(cents) {
      // Match Horizon's `money_with_currency` shape used elsewhere in the
      // theme: leading symbol from Intl.NumberFormat, trailing ISO code.
      return `${this.formatter.format(cents / 100)} ${this.currency}`;
    }

    _writePriceSlots(unit, math) {
      // Main PDP price (<product-price>) carries the full "unit × qty =
      // total" overlay. Both regular and sale sub-slots get the same
      // content; only the visible one (.price__hidden toggling) ends up
      // shown.
      //
      // When math is null (qty=1) the slot is just a text node, so it
      // looks identical to Horizon's server-rendered output. When math
      // is present, the slot becomes two spans so the trailing math can
      // render smaller / top-aligned via the CSS in the snippet.
      this.scope.querySelectorAll('product-price .price').forEach((el) => {
        if (math == null) {
          el.textContent = unit;
          return;
        }
        // Unit goes in as a plain text node — it inherits the slot's
        // headline type. Only the math suffix needs a styling hook
        // (smaller / top-aligned), so it's the one element that gets
        // a className.
        const unitText = document.createTextNode(unit);
        const mathSpan = document.createElement('span');
        mathSpan.className = 'bc-price-total__math';
        mathSpan.textContent = math;
        el.replaceChildren(unitText, mathSpan);
      });

      // The sticky add-to-cart bar shows ONLY the unit price — never the
      // "× qty = total" math. The bar is height-constrained (a wrapped math
      // line would shove the mini webbing diagram out of place), and the
      // compact headline price reads cleaner there. Always a plain text node.
      this.scope.querySelectorAll('.sticky-add-to-cart__price .price').forEach((el) => {
        el.textContent = unit;
      });
    }
  }

  customElements.define('bc-price-total', BcPriceTotal);
})();
